import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isSpatialLayerDynamic } from '@rsos/components/capstone/common/Map/utils/spatialLayersHelpers';
import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';
import { toggleSpatialLayer } from '../../actions/spatialLayers';
import { trackAirAmbulanceLayerToggle } from '../../gainsightTrackings/airAmbulanceTrackings';
import { LayerCheckboxProps, RootState } from './LayerCheckbox.types';
import NormalizedLayerCheckbox from './NormalizedLayerCheckbox';
import useToggleGuard from './useToggleGuard';

const STATIC_LAYERS_CARD = 'staticLayersCard';

declare const closeCard: (cardName: string) => {
  type: string;
  payload: string;
};
declare const setMapCardData: (data: unknown) => {
  type: string;
  payload: unknown;
};

const AIR_AMBULANCE_HELICOPTER_DYNAMIC_LAYER = 'gmr_air';

/**
 * Component for handling the checkbox of a spatial layer
 */
const LayerCheckbox: React.FC<LayerCheckboxProps> = ({ layer }) => {
  const dispatch = useDispatch();
  const isLayerDynamic = isSpatialLayerDynamic(layer);
  const layerID = layer.id;
  const layerKey = layer.key;
  const layerData = useSelector(
    (state: RootState) => state.mapCard?.mapCardData?.layerData,
  );
  const { irpVersion } = useSelector((state: RootState) => state.sinatra.irp);

  const handleCheckboxOnChange = useCallback(() => {
    // layer.isChecked is in previous state here before reset
    const willBeVisible = !layer.isChecked;
    const onOffText = willBeVisible ? 'on' : 'off';
    const { name, source } = layer;

    if (!isLayerDynamic && source === layerData?.source) {
      dispatch(closeCard(STATIC_LAYERS_CARD));
      dispatch(setMapCardData(null));
    } else {
      //TODO: add handling for dynamic layers
    }

    gspx.trackEvent({
      name: 'map_turn_spatial_layer',
      type: GPX_EVENT_TYPES.INFO,
      feature: GPX_FEATURES.LEGACY,
      capability: GPX_CAPABILITIES.LEGACY,
      route: GPX_ROUTES.MAP,
      additionalKeys: {
        legacy_category: 'Data Map',
        legacy_name: `Turn ${onOffText} ${name}`,
        Category: 'Spatial Layer',
        'Launched date': new Date(),
        irpVersion,
        layerName: name,
        visibility_state: willBeVisible ? 'show' : 'hide',
      },
    });

    if (layerKey === AIR_AMBULANCE_HELICOPTER_DYNAMIC_LAYER) {
      trackAirAmbulanceLayerToggle({
        layerId: AIR_AMBULANCE_HELICOPTER_DYNAMIC_LAYER,
        isVisible: willBeVisible,
      });
    }

    dispatch(toggleSpatialLayer(layerKey, isLayerDynamic, willBeVisible));
  }, [
    dispatch,
    layer,
    layerKey,
    isLayerDynamic,
    layerData?.source,
    irpVersion,
  ]);

  const guardedCheckboxOnChange = useToggleGuard(handleCheckboxOnChange);

  // Regular single checkbox for layers without sublayers or non-GIS layers
  return (
    <NormalizedLayerCheckbox
      layer={layer}
      layerID={layerID}
      handleCheckboxOnChange={guardedCheckboxOnChange}
    />
  );
};

export default LayerCheckbox;
